import {
  type ComponentProps,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
} from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { type Option } from "./types";

interface OptionWithCheckbox<T> extends Option<T> {
  checkbox?: ReactNode;
}

interface Props<T> extends ComponentProps<"div"> {
  option: OptionWithCheckbox<T>;
  size: "sm" | "md" | "lg";
  className?: string;
}

const _OptionItem = <T,>(
  { option, size, className, ...props }: Props<T>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  return (
    <div
      ref={ref}
      className={twMerge(
        "absolute top-0 left-0 w-full px-4 flex items-center gap-2",
        "text-sm cursor-pointer rounded",
        "data-[disabled]:text-v2-gray/200 data-[disabled]:bg-white data-[disabled]:cursor-auto",
        "data-[focus]:bg-v2-action-tertiary data-[selected]:bg-v2-action-secondary",
        "data-[selected]:font-semibold",
        clsx({
          "h-8": size === "sm",
          "h-9": size === "md",
          "h-10": size === "lg",
        }),
        className,
      )}
      {...props}
    >
      {option.checkbox}
      {option.icon && (
        <span
          className={clsx("inline-flex items-center justify-center w-4 h-4", {
            "text-v2-action": !option.disabled,
            "text-v2-gray/200": option.disabled,
          })}
        >
          {option.icon}
        </span>
      )}
      <span className="truncate">{option.label}</span>
    </div>
  );
};

// workaround to correctly infer generic type for forward reffed component
export const OptionItem = forwardRef(_OptionItem) as <T>(
  props: Props<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof _OptionItem>;
